import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";

// Import icons
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import { getExportProductPrices } from "../../../services/ExportPrice";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  textAlign: "center",
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.08)",
}));

// Custom TableItem wrapper that adds row highlighting
const HighlightedTableItem = styled(TableItem)(({ theme }) => ({
  "& .MuiTableRow-root": {
    transition: "all 0.2s ease",
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
  },
  // Highlight rows that have the warning icon (no pricing)
  "& .MuiTableRow-root:has(.no-pricing-indicator)": {
    backgroundColor: alpha("#ff9800", 0.08),
    borderLeft: `4px solid ${alpha("#ff9800", 0.6)}`,
  },
  "& .MuiTableRow-root:has(.no-pricing-indicator):hover": {
    backgroundColor: alpha("#ff9800", 0.15),
  },
}));

let _exProductPriceListState = null;

function ExProductPriceList() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(() => _exProductPriceListState?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    totalProducts: 0,
    withRetailPrice: 0,
    withWholesalePrice: 0,
    withNoPricing: 0,
    avgRetailProfit: 0,
    avgWholesaleProfit: 0,
  });

  // Filter state
  const [filter, setFilter] = useState(() => _exProductPriceListState?.filter ?? {
    _barcode: "",
    _product: "",
    _status: "",
    _warehouseId: null,
    _priceType: "",
  });

  // Table columns configuration
  const columns = [
    {
      id: "indicator",
      label: "",
      minWidth: 30,
      format: (value) => value, // This will render the warning icon
    },
    { id: "barcode", label: "Barcode", minWidth: 100 },
    { id: "productName", label: "Product", minWidth: 150 },
    {
      id: "retailPrice",
      label: "Retail Price",
      minWidth: 120,
      align: "right",
      format: (value) =>
        value ? (
          <Chip
            label={`Rs. ${value.toFixed(2)}`}
            color="success"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip
            label="Not Set"
            size="small"
            variant="outlined"
            color="default"
          />
        ),
    },
    {
      id: "wholesalePrice",
      label: "Wholesale Price",
      minWidth: 120,
      align: "right",
      format: (value) =>
        value ? (
          <Chip
            label={`Rs. ${value.toFixed(2)}`}
            color="info"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip
            label="Not Set"
            size="small"
            variant="outlined"
            color="default"
          />
        ),
    },
    {
      id: "totalCost",
      label: "Current Cost",
      minWidth: 120,
      align: "right",
      format: (value) =>
        value ? (
          <Typography variant="body2" fontWeight="medium">
            Rs. {value.toFixed(2)}
          </Typography>
        ) : (
          <Chip
            label="Not Set"
            size="small"
            variant="outlined"
            color="warning"
          />
        ),
    },
    {
      id: "retailProfitPerUnit",
      label: "Retail Profit/Unit",
      minWidth: 130,
      align: "right",
      format: (value, row) => {
        if (!row?.retailPrice || !row?.totalCost) return "-";
        const profit = value;
        const percentage = ((profit / row.totalCost) * 100).toFixed(1);
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold" color="success.main">
              Rs. {profit.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({percentage}%)
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "wholesaleProfitPerUnit",
      label: "Wholesale Profit/Unit",
      minWidth: 150,
      align: "right",
      format: (value, row) => {
        if (!row?.wholesalePrice || !row?.totalCost) return "-";
        const profit = value;
        const percentage = ((profit / row.totalCost) * 100).toFixed(1);
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold" color="info.main">
              Rs. {profit.toFixed(2)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({percentage}%)
            </Typography>
          </Box>
        );
      },
    },
    { id: "action", label: "Actions", minWidth: 100 },
  ];

  // Create data for table
  function createData(
    barcode,
    productName,
    retailPrice,
    wholesalePrice,
    totalCost,
    retailProfitPerUnit,
    wholesaleProfitPerUnit,
    productId,
    hasNoPricing,
  ) {
    return {
      // Add indicator column - warning icon for rows without pricing
      indicator: hasNoPricing ? (
        <Tooltip title="No pricing assigned">
          <WarningIcon
            className="no-pricing-indicator"
            sx={{ color: "#ff9800", fontSize: 20 }}
          />
        </Tooltip>
      ) : null,
      barcode,
      productName,
      retailPrice,
      wholesalePrice,
      totalCost,
      retailProfitPerUnit,
      wholesaleProfitPerUnit,
      action: productId,
      productId,
      hasNoPricing,
    };
  }

  // Initial data load
  useEffect(() => {
    _exProductPriceListState = null;
    fetchData();
  }, [page, rowsPerPage]);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      let result = await getExportProductPrices(page, rowsPerPage, filter);

      if (result && result !== "Something went wrong...") {
        let priceList = result.content;

        // Set total pages and rows for pagination
        setTotalPages(result.totalPages);
        setTotalRows(result.totalElements);

        const newSet = [];
        let withRetail = 0;
        let withWholesale = 0;
        let withNoPricing = 0;
        let totalRetailProfit = 0;
        let totalWholesaleProfit = 0;
        let retailCount = 0;
        let wholesaleCount = 0;

        if (priceList) {
          for (let x = 0; x < priceList.length; x++) {
            const item = priceList[x];
            const retailPrice = item.currentRetailPrice;
            const wholesalePrice = item.currentWholesalePrice;
            const totalCost = item.currentCost?.totalCost || item.totalCost;

            // Check if product has NO pricing at all (highlight these)
            const hasNoPricing = !retailPrice && !wholesalePrice;
            if (hasNoPricing) {
              withNoPricing++;
            }

            // Calculate profits
            let retailProfit = null;
            let wholesaleProfit = null;

            if (retailPrice && totalCost) {
              retailProfit = retailPrice - totalCost;
              totalRetailProfit += retailProfit;
              retailCount++;
              withRetail++;
            }

            if (wholesalePrice && totalCost) {
              wholesaleProfit = wholesalePrice - totalCost;
              totalWholesaleProfit += wholesaleProfit;
              wholesaleCount++;
              withWholesale++;
            }

            newSet.push(
              createData(
                item.barcode,
                item.productName,
                retailPrice,
                wholesalePrice,
                totalCost,
                retailProfit,
                wholesaleProfit,
                item.barcode,
                hasNoPricing,
              ),
            );
          }
        }

        // Update statistics
        setStats({
          totalProducts: result.totalElements,
          withRetailPrice: withRetail,
          withWholesalePrice: withWholesale,
          withNoPricing: withNoPricing,
          avgRetailProfit:
            retailCount > 0 ? totalRetailProfit / retailCount : 0,
          avgWholesaleProfit:
            wholesaleCount > 0 ? totalWholesaleProfit / wholesaleCount : 0,
        });

        setRows(newSet);
      }
    } catch (error) {
      console.error("Error fetching export product prices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
  };

  // Handle action for table actions
  const handleAction = async (event, value) => {
    switch (event) {
      case "view":
        _exProductPriceListState = { page, filter };
        history.push("/template/ex_product_price_view/" + value);
        break;
      default:
        return;
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _barcode: "",
      _product: "",
      _status: "",
      _warehouseId: null,
      _priceType: "",
    });
    setPage(0);
    fetchData();
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <StyledPaper elevation={0}>
        <HeaderCard>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <PriceCheckIcon fontSize="large" />
            </Grid>
            <Grid item xs>
              <Typography variant="h5" fontWeight="bold">
                Product Pricing
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                View and manage product prices, costs, and profit margins
              </Typography>
            </Grid>
          </Grid>
        </HeaderCard>

        {/* Statistics Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {stats.totalProducts}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Products
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {stats.withRetailPrice}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                With Retail Price
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {stats.withWholesalePrice}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                With Wholesale Price
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {stats.withNoPricing}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Without Pricing
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatCard>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                Rs. {stats.avgRetailProfit.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Retail Profit
              </Typography>
            </StatCard>
          </Grid>
        </Grid>

        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            placeholder="Search by product name or barcode..."
            variant="outlined"
            size="small"
            sx={{ width: "40%" }}
            value={filter._product}
            onChange={(e) => {
              handleFilterChange("_product", e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "action.active", mr: 1 }} />
              ),
            }}
          />
          <Stack direction="row" spacing={2}>
            <Tooltip title="Toggle Filters">
              <ActionButton
                variant="outlined"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={toggleFilters}
              >
                Filters
              </ActionButton>
            </Tooltip>
            <Chip
              label={`${totalRows} Items`}
              variant="outlined"
              color="primary"
            />
          </Stack>
        </Box>

        {showFilters && (
          <FilterContainer>
            <Typography variant="subtitle2" fontWeight="bold" mb={2}>
              Advanced Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <InputField
                  name="_barcode"
                  value={filter._barcode}
                  onChange={(event, newInputValue) =>
                    handleFilterChange(event, newInputValue)
                  }
                  type="text"
                  label="Barcode"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={filter._status}
                  onChange={(e) =>
                    handleFilterChange("_status", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Price Type"
                  value={filter._priceType}
                  onChange={(e) =>
                    handleFilterChange("_priceType", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="RETAIL">Retail Only</MenuItem>
                  <MenuItem value="WHOLESALE">Wholesale Only</MenuItem>
                  <MenuItem value="BOTH">Both Prices Set</MenuItem>
                  <MenuItem value="NONE">No Prices Set</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                <Tooltip title="Reset Filters">
                  <ActionButton
                    variant="outlined"
                    color="secondary"
                    startIcon={<RestartAltIcon />}
                    onClick={resetFilters}
                  >
                    Reset
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Apply Filters">
                  <ActionButton
                    variant="contained"
                    color="primary"
                    onClick={fetchData}
                  >
                    Apply Filters
                  </ActionButton>
                </Tooltip>
              </Stack>
            </Box>
          </FilterContainer>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Info Box for Color Coding */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: alpha("#ff9800", 0.1),
            border: `1px solid ${alpha("#ff9800", 0.3)}`,
          }}
        >
          <WarningIcon sx={{ color: "#ff9800" }} />
          <Typography variant="body2" color="text.secondary">
            <strong>Rows with warning icon</strong> indicate products without
            any assigned prices (retail or wholesale). Click "View" to assign
            pricing.
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 5 }}>
            <TrendingUpIcon
              sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
            />
            <Typography variant="body1" color="textSecondary">
              No pricing data found. Try adjusting your filters or add prices to
              products.
            </Typography>
          </Box>
        ) : (
          <HighlightedTableItem
            columns={columns}
            rows={rows}
            page={page}
            setPage={setPage}
            tablePagin={false}
            totalPages={totalPages}
            handleAction={handleAction}
            showActions={{
              view: "View Pricing Details",
            }}
            actionRoles={{
              view: [
                "PRODUCT_PRICE_VIEW",
                "PRODUCT_PRICE_UPDATE",
                "PRODUCT_PRICE_CREATE",
                "PRODUCT_PRICE_DELETE",
                "ROLE_ADMIN",
              ],
            }}
          />
        )}

        {totalPages > 1 && (
          <PaginationContainer>
            <Pagination
              count={totalPages}
              page={page + 1}
              variant="outlined"
              shape="rounded"
              color="primary"
              onChange={handleChange}
              size="medium"
            />
          </PaginationContainer>
        )}
      </StyledPaper>
    </>
  );
}

export default ExProductPriceList;
